import { useRef, useCallback } from 'react'

interface ResizeHandleProps {
    side: 'left' | 'right'
    onResize: (delta: number) => void
    onCollapse: () => void
    minWidth: number
}

export function ResizeHandle({ side, onResize, onCollapse, minWidth }: ResizeHandleProps) {
    const startXRef = useRef(0)
    const accumulatedRef = useRef(0)

    const onPointerDown = useCallback((e: React.PointerEvent) => {
        e.preventDefault()
        e.currentTarget.setPointerCapture(e.pointerId)
        startXRef.current = e.clientX
        accumulatedRef.current = 0
    }, [])

    const onPointerMove = useCallback((e: React.PointerEvent) => {
        if (!e.currentTarget.hasPointerCapture(e.pointerId)) return
        const dx = e.clientX - startXRef.current
        startXRef.current = e.clientX

        // For left panel: dragging right = wider, for right panel: dragging left = wider
        const delta = side === 'left' ? dx : -dx
        accumulatedRef.current += delta
        onResize(delta)
    }, [side, onResize])

    const onPointerUp = useCallback((e: React.PointerEvent) => {
        e.currentTarget.releasePointerCapture(e.pointerId)
        // If dragged significantly inward past min, collapse
        if (accumulatedRef.current < -minWidth * 0.3) {
            onCollapse()
        }
    }, [onCollapse, minWidth])

    return (
        <div
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            className="shrink-0 relative z-10 group"
            style={{
                width: '6px',
                cursor: 'col-resize',
                touchAction: 'none',
            }}
        >
            {/* Visible line on hover */}
            <div
                className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px transition-all duration-150 group-hover:w-0.5 group-active:w-1"
                style={{
                    background: 'color-mix(in srgb, var(--border) 40%, transparent)',
                }}
            />
            <div
                className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-0.5 opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-150"
                style={{ background: 'var(--accent)' }}
            />
        </div>
    )
}
